import { Component, OnInit, Inject, Optional } from '@angular/core';
import { ReassignmentService } from '../../services/reassignment/reassignment.service';
import { MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import * as ExcelJS from 'exceljs';
import { UserService } from 'src/app/services/user/user.service';

export interface ValidationStep {
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
}

@Component({
  selector: 'app-validation-progress-modal',
  templateUrl: './validation-progress-modal.component.html',
  styleUrls: ['./validation-progress-modal.component.scss'],
})
export class ValidationProgressModalComponent implements OnInit {

  isDone = false;
  hasError = false;
  errorMessage = '';
  errorMessages: string[] = [];
  parsedData: any[] = [];

  steps: ValidationStep[] = [
    { label: 'Reading Excel File', status: 'pending' },
    { label: 'Validating File Format', status: 'pending' },
    { label: 'Checking Column Headers', status: 'pending' },
    { label: 'Validating Fields', status: 'pending' },
  ];
  userId: string;
  currentStepIndex = 0;

  constructor(
    private dialogRef: MatDialogRef<ValidationProgressModalComponent>,
    private userService: UserService,
    private reassignmentService: ReassignmentService,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  ngOnInit() {
    this.userService.getUser().subscribe((res: any) => {
      this.userId = res.uuid;
    });
    this.runValidation();
  }

  async runValidation() {
    try {
      /* ------------------------------------------------
       * STEP 0 – FILE NAME VALIDATION (FIRST & STRICT)
       * ------------------------------------------------ */
      const fileName = this.data?.file?.name ?? '';

      const FILE_NAME_REGEX =
        /^ReassignmentRecords_\d{2}_\d{2}_\d{2}( \(\d+\))?\.xlsx$/i;

      /* ------------------------------------------------
       * STEP 1 – READ EXCEL FILE
       * ------------------------------------------------ */
      await this.activateStep(0);
      const rows = await this.readExcelFile(this.data?.file);
      await this.completeStep(0);

      /* ------------------------------------------------
       * STEP 2 – EMPTY FILE CHECK
       * ------------------------------------------------ */
      await this.activateStep(1);
      if (!FILE_NAME_REGEX.test(fileName)) {
        throw new Error('invalid_filename');
      }

      if (!rows || rows.length === 0) {
        throw new Error('Excel file is empty');
      }
      await this.completeStep(1);

      /* ------------------------------------------------
       * STEP 3 – STRICT HEADER VALIDATION (ALL REQUIRED)
       * ------------------------------------------------ */
      await this.activateStep(2);
      const headers: string[] = rows[0];
      console.log("headers", headers);

      const requiredHeaders = [
        'Folio No',
        'Application No',
        'Status',
        'Investor Name',
        'PAN',
        'Distributor ARN',
        'Existing RMName',
        'Existing RMEmail',
        'Existing CXOName',
        'Existing CXOEmail',
        'Existing CXOMobile',
        'Existing RMCode',
        'New RM Name',
        'New RM Email',
        'New RM Code',
        'New CXO Name',
        'New CXO Email',
        'New CXO Mobile'
      ];

      const missingHeaders = requiredHeaders.filter(
        h => !headers.includes(h)
      );

      if (missingHeaders.length > 0) {
        throw new Error(`Missing columns: ${missingHeaders.join(', ')}`);
      }

      await this.completeStep(2);

      /* ------------------------------------------------
       * STEP 4 – ROW LEVEL VALIDATION
       * ------------------------------------------------ */
      await this.activateStep(3);
      const validationErrors: string[] = [];

      this.parsedData = rows.slice(1).map((row: any[]) => {
        const obj: any = {};
        rows[0].forEach((header: string, index: number) => {
          obj[header] = this.getPlainText(row[index]);
        });
        return obj;
      });

      console.log('parsedData', this.parsedData);

      const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const MOBILE_REGEX = /^\d{10}$/;

      this.parsedData.forEach((row, index) => {

        const rmName = row['New RM Name']?.toString().trim();

        // const rmEmail = row['New RM Email']
        //   ?.toString()
        //   .replace(/\s+/g, '')
        //   .trim();

        const rmEmail = this.getPlainText(row['New RM Email'])
          .replace(/\s+/g, '')
          .trim();

        const rmCode = row['New RM Code']?.toString().trim();

        const cxoName = row['New CXO Name']?.toString().trim();

        // const cxoEmail = row['New CXO Email']
        //   ?.toString()
        //   .replace(/\s+/g, '')
        //   .trim();

        const cxoEmail = this.getPlainText(row['New CXO Email'])
          .replace(/\s+/g, '')
          .trim();

        const cxoMobile = row['New CXO Mobile']?.toString().trim();

        const rmTouched = !!(rmName || rmEmail || rmCode);
        const cxoTouched = !!(cxoName || cxoEmail || cxoMobile);

        /* ---------- AT LEAST ONE REQUIRED ---------- */
        if (!rmTouched && !cxoTouched) {
          validationErrors.push(
            `Row ${index + 2}: Either New RM or New CXO details must be filled`
          );
          return;
        }

        /* ---------- RM VALIDATION ---------- */
        if (rmTouched) {
          if (!rmName) {
            validationErrors.push(`Row ${index + 2}: New RM Name is missing`);
          }

          console.log('rmEmail', rmEmail);
          console.log('EMAIL_REGEX.test(rmEmail)', EMAIL_REGEX.test(rmEmail));

          if (!rmEmail) {
            validationErrors.push(`Row ${index + 2}: New RM Email is missing`);
          } else if (!EMAIL_REGEX.test(rmEmail)) {
            validationErrors.push(
              `Row ${index + 2}: New RM Email is invalid (missing @ or .)`
            );
          }

          if (!rmCode) {
            validationErrors.push(`Row ${index + 2}: New RM Code is missing`);
          }
        }

        /* ---------- CXO VALIDATION ---------- */
        if (cxoTouched) {
          if (!cxoName) {
            validationErrors.push(`Row ${index + 2}: New CXO Name is missing`);
          }

          if (!cxoEmail) {
            validationErrors.push(`Row ${index + 2}: New CXO Email is missing`);
          } else if (!EMAIL_REGEX.test(cxoEmail)) {
            validationErrors.push(
              `Row ${index + 2}: New CXO Email is invalid (missing @ or .)`
            );
          }

          if (!cxoMobile) {
            validationErrors.push(`Row ${index + 2}: New CXO Mobile is missing`);
          } else if (!MOBILE_REGEX.test(cxoMobile)) {
            validationErrors.push(
              `Row ${index + 2}: New CXO Mobile must be a valid 10-digit number`
            );
          }
        }
      });

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }
      await this.completeStep(3);
      this.isDone = true;

    } catch (err: any) {
      this.hasError = true;
      this.failStep(this.currentStepIndex);

      if (err.message === 'invalid_filename') {
        this.errorMessages = [
          'Invalid file format. Please upload correct Excel template.'
        ];
        return;
      }

      if (err.message !== 'validation_failed') {
        this.errorMessages = [err.message || 'Validation failed'];
      }
    }
  }

  failStep(failedIndex: number) {
    const index =
      typeof failedIndex === 'number'
        ? failedIndex
        : this.steps.findIndex(s => s.status === 'active');

    this.steps.forEach((step, i) => {
      if (step.status === 'active') {
        step.status = 'error';
      }

      if (i > index) {
        step.status = 'error';
      }
    });
  }

  readExcelFile(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('No file provided'));
        return;
      }
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          const buffer = e.target.result;
          const workbook = new ExcelJS.Workbook();
          await workbook.xlsx.load(buffer);
          const worksheet = workbook.worksheets[0];
          const rows: any[] = [];
          worksheet.eachRow(row => {
            rows.push((row.values as any[]).slice(1));
          });
          resolve(rows);
        } catch (err) {
          reject(new Error('Failed to read Excel file'));
        }
      };
      reader.onerror = () => reject(new Error('File read error'));
      reader.readAsArrayBuffer(file);
    });
  }

  activateStep(index: number): Promise<void> {
    this.currentStepIndex = index;
    this.steps[index].status = 'active';
    return new Promise(resolve => setTimeout(resolve, 600));
  }

  completeStep(index: number): Promise<void> {
    this.steps[index].status = 'done';
    return new Promise(resolve => setTimeout(resolve, 300));
  }

  proceed() {

    this.hasError = false;
    this.errorMessages = [];
    this.errorMessage = '';

    const headerMap: { [key: string]: string } = {
      'Folio No': 'folioNo',
      'Application No': 'applicationNo',
      'Status': 'status',
      'Investor Name': 'investorName',
      'PAN': 'pan',
      'Fund Name': 'fundName',
      'Distributor ARN': 'distributorARN',
      'Distributor Name': 'distributorName',
      'Existing RMName': 'existingRMName',
      'Existing RMEmail': 'existingRMEmail',
      'Existing CXOName': 'existingCXOName',
      'Existing CXOEmail': 'existingCXOEmail',
      'Existing CXOMobile': 'existingCXOMobile',
      'Existing RMCode': 'existingRMCode',
      'New RM Name': 'newRMName',
      'New RM Email': 'newRMEmail',
      'New RM Code': 'newRMCode',
      'New CXO Name': 'newCXOName',
      'New CXO Email': 'newCXOEmail',
      'New CXO Mobile': 'newCXOMobile'
    };

    const investorList = this.parsedData.map(row => {
      const isRMChange =
        row['New RM Name'] ||
        row['New RM Email'] ||
        row['New RM Code'];

      const isCXOChange =
        row['New CXO Name'] ||
        row['New CXO Email'] ||
        row['New CXO Mobile'];

      return {
        applicationNo: row['Application No'],
        folioNo: row['Folio No'],
        investorName: row['Investor Name'],
        pan: row['PAN'],
        fundName: row['Fund Name'],

        distributorARN: row['Distributor ARN'],
        distributorName: row['Distributor Name'],

        existingRMName: row['Existing RMName'],
        existingRMEmail: row['Existing RMEmail'],
        existingRMCode: row['Existing RMCode'],

        existingCXOName: row['Existing CXOName'],
        existingCXOEmail: row['Existing CXOEmail'],
        existingCXOMobile: row['Existing CXOMobile'],

        newRMName: isRMChange ? row['New RM Name'] : row['Existing RMName'],
        newRMEmail: isRMChange ? this.getPlainText(row['New RM Email']).replace(/\s+/g, '').trim() : row['Existing RMEmail'],
        newRMCode: isRMChange
          ? String(row['New RM Code'])
          : row['Existing RMCode'],



        newCXOName: isCXOChange ? row['New CXO Name'] : row['Existing CXOName'],
        newCXOEmail: isCXOChange ? this.getPlainText(row['New CXO Email']).replace(/\s+/g, '').trim() : row['Existing CXOEmail'],
        newCXOMobile: isCXOChange
          ? String(row['New CXO Mobile'])
          : row['Existing CXOMobile'],
        status: row['Status'],
        reason: null
      };
    });

    const payload = {
      DistributorEntityName: this.data?.distributorEntityName,
      UserId: this.userId,
      InvestorList: investorList
    };

    this.reassignmentService.validateFile(payload).subscribe({
      next: (validateRes: any) => {

        if (!validateRes?.success) {
          this.hasError = true;
          this.errorMessages = validateRes?.errors || ['Validation failed'];
          return;
        }

        this.dialogRef.close({
          showValidationSummary: true,
          validationData: {
            totalRecords: validateRes.data.totalRecords,
            validRecords: validateRes.data.validRecords,
            invalidRecords: validateRes.data.invalidRecords,
            noChangeRecords: validateRes.data.noChangeRecords
          },

          updateList: validateRes.data.updateList,
          validationPayload: payload
        });
      },
      error: () => {
        this.hasError = true;
        this.errorMessages = ['Validation service failed'];
      }
    });
  }

  close(): void {
    this.dialogRef.close({
      validationFailed: true
    });
  }

  private getPlainText(cell: any): string {
    if (cell === null || cell === undefined) return '';
    if (typeof cell === 'object') {
      if (Array.isArray(cell.richText)) return cell.richText.map((r: any) => r.text ?? '').join('');
      if ('text' in cell) return String(cell.text ?? '');
      if ('result' in cell) return String(cell.result ?? '');
    }
    return cell.toString();
  }
}
